import { faCircle } from '@fortawesome/free-regular-svg-icons';
import { faDiamondExclamation } from '@fortawesome/pro-solid-svg-icons';
import { intersection } from 'lodash';

import { COMPLETED_ORDER_STATUSES } from '@constants/constants';
import { IconDefinition } from '@customTypes/common';
import {
  DisplayableOrderStatus,
  Order,
  OrderResultFinding,
  OrderStatus,
  ResultsType,
  ToplineResult,
} from '@customTypes/order';
import { BaseRequisitionOrder } from '@customTypes/requisition';

interface OrderStatusConfig {
  label: string;
  filterValue: OrderStatus;
  statuses: ReadonlyArray<OrderStatus>;
}

export const ORDER_STATUS_CONFIG: ReadonlyRecord<DisplayableOrderStatus, OrderStatusConfig> = {
  [DisplayableOrderStatus.RECEIVED]: {
    label: 'Order Received',
    filterValue: OrderStatus.AWAITING_SAMPLE,
    statuses: [OrderStatus.SUBMITTED, OrderStatus.APPROVED, OrderStatus.AWAITING_SAMPLE],
  },
  [DisplayableOrderStatus.LAB_PROCESSING]: {
    label: 'Lab Processing',
    filterValue: OrderStatus.ANALYZING,
    statuses: [OrderStatus.ANALYZING, OrderStatus.REFERRAL_LAB_LD_REVIEW],
  },
  [DisplayableOrderStatus.CLINICIAN_REVIEW]: {
    label: 'Clinical Summary Ready',
    filterValue: OrderStatus.CLINICIAN_REVIEW,
    statuses: [OrderStatus.CLINICIAN_REVIEW],
  },
  [DisplayableOrderStatus.COMPLETED]: {
    label: 'Complete',
    filterValue: OrderStatus.COMPLETED,
    statuses: [OrderStatus.COMPLETED],
  },
  [DisplayableOrderStatus.CANCELED]: {
    label: 'Canceled',
    filterValue: OrderStatus.CANCELED,
    statuses: [OrderStatus.CANCELED],
  },
};

interface ResultTypeConfig {
  label: string;
  icon: IconDefinition | undefined;
  findings: ReadonlyArray<OrderResultFinding>;
}

export const RESULT_TYPE_CONFIG: ReadonlyRecord<ResultsType, ResultTypeConfig> = {
  [ResultsType.ACTIONABLE_RESULT]: {
    label: 'Actionable Result',
    icon: faDiamondExclamation,
    findings: [
      OrderResultFinding.ELEVATED_RISK,
      OrderResultFinding.HIGH_RISK,
      OrderResultFinding.INCREASED_RISK,
      OrderResultFinding.INTERMEDIATE_RISK,
      OrderResultFinding.POSITIVE,
    ],
  },
  [ResultsType.AVERAGE_RISK_OR_NEGATIVE]: {
    label: 'Average risk or negative',
    icon: faCircle,
    findings: [
      OrderResultFinding.AVERAGE_RISK,
      OrderResultFinding.BORDERLINE_RISK,
      OrderResultFinding.DECREASED_RISK,
      OrderResultFinding.LOW_RISK,
      OrderResultFinding.NEGATIVE,
    ],
  },
  [ResultsType.NO_DATA_AVAILABLE]: {
    label: 'No data available',
    icon: undefined,
    findings: [
      OrderResultFinding.ALTERED_DOSE,
      OrderResultFinding.CONTRAINDICATED,
      OrderResultFinding.MAJOR_IMPACT,
      OrderResultFinding.MINIMAL_IMPACT,
      // Genotyped outside the e2/e3/e4 set. Not a risk call.
      OrderResultFinding.NOT_REPORTABLE,
      // QC did not pass, so no genotype was established.
      OrderResultFinding.NOT_ANALYZED,
    ],
  },
};

/**
 * Returns whether the provided order's status is in a completed state
 */
export const isOrderComplete = (order: BaseRequisitionOrder): boolean =>
  COMPLETED_ORDER_STATUSES.includes(order.status);

// TODO: Replace usage with `isOrderComplete` once verified `OrderStatus.REPORTED` is not used
export const isOrderReported = isOrderComplete;

export const getOrderResult = (order?: Order | null): ResultsType => {
  return getResultType(order?.results ?? []);
};

export const getResultType = (results: ToplineResult[]): ResultsType => {
  const findings = results.map(result => result.finding);

  if (intersection(findings, RESULT_TYPE_CONFIG.ACTIONABLE_RESULT.findings).length) {
    return ResultsType.ACTIONABLE_RESULT;
  }

  if (intersection(findings, RESULT_TYPE_CONFIG.AVERAGE_RISK_OR_NEGATIVE.findings).length) {
    return ResultsType.AVERAGE_RISK_OR_NEGATIVE;
  }

  return ResultsType.NO_DATA_AVAILABLE;
};

export const getIconByResult = (result: ResultsType): IconDefinition | undefined => {
  const resultConfig = RESULT_TYPE_CONFIG[result];
  return resultConfig?.icon;
};
